import { z } from 'zod';
import { capabilitySchema, type Capability } from './roles.js';
import { cidrSchema } from './site.js';

/**
 * Org-wide API keys.
 *
 * `read` maps to viewer capabilities and `readwrite` to admin capabilities —
 * both minus user and organization management, which no key ever carries.
 * `custom` carries exactly the capabilities chosen when it was minted, which
 * is what an integration that should read the fleet and reboot nothing
 * actually needs: the two preset rungs are a ladder, and a ladder is the wrong
 * shape when the answer is "these four things".
 *
 * IP restriction is optional — a key with an empty allowlist answers from
 * anywhere, which is the default.
 */
export const apiKeyScopeSchema = z.enum(['read', 'readwrite', 'custom']);
export type ApiKeyScope = z.infer<typeof apiKeyScopeSchema>;

/**
 * The capabilities an integration or an AI assistant that manages subscribers'
 * ONUs needs, and nothing more — the recommended starting point for a `custom`
 * key handed to an MCP client.
 *
 * It is a STARTING LIST for the custom picker, deliberately not a fourth scope:
 * a new scope value would ripple through the database check constraint, the
 * key resolver and every client that reads `scope`, for what is really "tick
 * these nine". The picker intersects it with what the minter holds, so a
 * minter who lacks one of these is told which was left out rather than handed
 * a key that silently does less.
 *
 * Why each is in, and why the rest are out:
 *
 * - `olt.read` — the inventory: which ONUs exist, their optics, state and
 *   events, the chassis they hang off. Every other tool starts here.
 * - `device.read` — the ONU's own TR-069 view: WAN, LAN, WiFi, hosts.
 * - `site.read` — the plant around the ONU: zones, splitters, the light path.
 * - `device.write` — the CPE-side configuration writes: WiFi, WAN, presets.
 * - `device.wifi` — the ONU-addressed WiFi write (`POST /api/onus/:id/wifi`),
 *   which stores the SSID against the ONU and pushes it when the CPE informs.
 *   An assistant that sets a subscriber's WiFi should reach the same door the
 *   phone does, and that door asks for this name rather than `device.write`.
 * - `device.action` — summon, reboot, reset of ONE CPE at a time.
 * - `onu.provision` — the chassis-side subscriber work: authorise, resync,
 *   reboot, recreate, swap, management mode, OMCI WiFi.
 * - `onu.speedtest` — a TR-143 speed test on ONE subscriber's line, and the
 *   history of them: the assistant's answer to "is the line slow or is the
 *   customer's laptop". Reads three diagnostics objects; changes nothing.
 * - `olt.diagnose` — asking a chassis about itself: health, sync, probes —
 *   what "is it the ONU or the OLT?" needs, without any power to change it.
 * - `audit.read` — the assistant can read back what it (and others) did, and
 *   the drift findings the platform produced about the fleet.
 *
 * NOT `bulk.execute`: a fleet-wide run is a person's decision behind a typed
 * ladder. NOT `olt.manage`, `olt.backup`, `olt.restore`: the chassis itself —
 * onboarding, credentials, deletion, restores — is not subscriber work. NOT
 * `settings.manage`, `billing.manage`, `apikey.manage`: nothing about the
 * organization's configuration, money or credentials. `user.manage` and
 * `org.manage` never ride a key at all.
 */
export const ONU_MANAGEMENT_KEY_CAPABILITIES: readonly Capability[] = [
  'olt.read',
  'device.read',
  'site.read',
  'device.write',
  'device.wifi',
  'device.action',
  'onu.provision',
  'onu.speedtest',
  'olt.diagnose',
  'audit.read',
];

/** A single address is accepted and treated as a /32. */
export const ipOrCidrSchema = z
  .string()
  .regex(
    /^(\d{1,3}\.){3}\d{1,3}(\/([0-9]|[12][0-9]|3[0-2]))?$/,
    'expected an IPv4 address or CIDR such as 102.215.34.8 or 10.0.0.0/24',
  );

export const apiKeySchema = z.object({
  id: z.string(),
  /** Null for a platform key, which spans every organization. */
  orgId: z.string().nullable(),
  orgName: z.string().nullable().optional(),
  /**
   * Superadmin-minted key that works across every organization. It still cannot
   * manage users or organizations — that holds for every API key.
   */
  platform: z.boolean(),
  name: z.string(),
  tokenPrefix: z.string(),
  scope: apiKeyScopeSchema,
  /**
   * What this key may actually do, resolved — the preset list for `read` and
   * `readwrite`, the chosen list for `custom`.
   *
   * Returned on every key rather than only the custom ones so the page can
   * show one honest answer to "what can this do" without re-deriving the
   * mapping in the browser, where it would drift from the server's.
   */
  capabilities: z.array(capabilitySchema),
  allowedIps: z.array(z.string()),
  status: z.enum(['active', 'disabled']),
  lastUsedAt: z.number().int().nullable(),
  lastUsedIp: z.string().nullable(),
  createdAt: z.number().int(),
  /** True when the full key can be revealed (behind a password re-prompt). */
  retrievable: z.boolean(),
});
export type ApiKey = z.infer<typeof apiKeySchema>;

/** Reveal the full key — the caller's own password re-authorises it. */
export const revealApiKeySchema = z.object({ password: z.string().min(1).max(200) });
export type RevealApiKeyRequest = z.infer<typeof revealApiKeySchema>;

export const createApiKeySchema = z.object({
  name: z.string().min(2).max(80),
  scope: apiKeyScopeSchema.default('read'),
  /**
   * Required for `custom`, ignored otherwise.
   *
   * The minter can only grant what they themselves hold — the same rule the
   * role matrix already enforces for people — and never `user.manage` or
   * `org.manage`, which stay off every key however they are minted: a
   * credential that can mint credentials or move tenants is a credential that
   * can escalate itself out of its own scope.
   */
  capabilities: z.array(capabilitySchema).max(32).default([]),
  allowedIps: z.array(ipOrCidrSchema).max(32).default([]),
  /** Superadmin only: mint a key that spans every organization. */
  platform: z.boolean().default(false),
});
export type CreateApiKeyRequest = z.infer<typeof createApiKeySchema>;

export const updateApiKeySchema = z.object({
  name: z.string().min(2).max(80).optional(),
  scope: apiKeyScopeSchema.optional(),
  /** Only meaningful alongside (or on an existing) `custom` scope. */
  capabilities: z.array(capabilitySchema).max(32).optional(),
  allowedIps: z.array(ipOrCidrSchema).max(32).optional(),
  status: z.enum(['active', 'disabled']).optional(),
});
export type UpdateApiKeyRequest = z.infer<typeof updateApiKeySchema>;

/** Returned exactly once, at creation. */
export const createdApiKeySchema = apiKeySchema.extend({ token: z.string() });
export type CreatedApiKey = z.infer<typeof createdApiKeySchema>;
